import React from 'react';
import FinexLogo from '../../components/FinexLogo';

/**
 * Printable / downloadable Stock Transfer document.
 *
 * Shares the visual language of the Purchase Order document (OrderPrintView):
 * teal accent letterhead, party blocks, refined line table and a min-height
 * flex column that fills an A4 page (signatures + footer pinned to the bottom).
 * Self-contained inline styles so it renders identically through
 * window.print() and html2pdf.
 *
 * Two variants driven by `received`:
 *  - issued   (false): No · SKU · Product · UOM · QTY
 *  - received (true) : adds Delivered + Variance (variance = received - sent,
 *                      red when short, green when over, "/" when exact), and the
 *                      receiver's name above the signature line.
 *
 * Party data:
 *   - From         → dbo.companies (issuer)
 *   - Issued from  → source dbo.locations
 *   - Issued to    → destination dbo.locations
 */

export interface TransferPrintRow {
  sku?: string;
  productName?: string;
  uomName?: string;
  sentQty?: number;
  receivedQty?: number;
}

export interface TransferPrintViewProps {
  lang?: 'al' | 'en';
  received?: boolean;
  company?: {
    companyName?: string | null;
    companyNumber?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    postalCode?: string | null;
  } | null;
  transferNumber?: string | number | null;
  date?: string | null;
  statusId?: number | null;
  fromLocation?: string | null;
  toLocation?: string | null;
  issuedBy?: string | null;
  receivedByName?: string | null;
  rows?: TransferPrintRow[];
}

const LABELS = {
  en: {
    title: 'STOCK TRANSFER',
    from: 'From',
    date: 'Date',
    transferNumber: 'Transfer number',
    issuedFrom: 'Issued from',
    issuedTo: 'Issued to',
    issuedBy: 'Issued by',
    no: 'No.',
    sku: 'SKU',
    product: 'Product',
    uom: 'UOM',
    qty: 'Qty',
    delivered: 'Delivered',
    variance: 'Variance',
    items: 'Items',
    totalQty: 'Total qty',
    acceptedBy: 'Accepted by',
    noItems: 'No items on this transfer.',
  },
  al: {
    title: 'TRANSFER I STOKUT',
    from: 'Nga',
    date: 'Data',
    transferNumber: 'Numri i transferit',
    issuedFrom: 'Nga lokacioni',
    issuedTo: 'Te lokacioni',
    issuedBy: 'Lëshuar nga',
    no: 'Nr.',
    sku: 'SKU',
    product: 'Produkti',
    uom: 'Njësia',
    qty: 'Sasia',
    delivered: 'Dorëzuar',
    variance: 'Diferenca',
    items: 'Artikuj',
    totalQty: 'Sasia totale',
    acceptedBy: 'Pranuar nga',
    noItems: 'Nuk ka artikuj në këtë transfer.',
  },
};

const STATUS: Record<'al' | 'en', Record<number, string>> = {
  en: { 1: 'Draft', 2: 'Sent', 3: 'Received', 4: 'Rejected', 5: 'Cancelled' },
  al: { 1: 'Skicë', 2: 'Dërguar', 3: 'Pranuar', 4: 'Refuzuar', 5: 'Anuluar' },
};

// Palette — restrained, one teal accent (the existing brand colour).
const ACCENT = '#0F7679';
const ACCENT_SOFT = '#EAF3F3';
const INK = '#1F2937';
const MUTED = '#6B7280';
const LINE = '#E6E8EB';

const join = (parts: Array<string | null | undefined>) =>
  parts.map((p) => (p == null ? '' : String(p).trim())).filter(Boolean).join(', ');

const TransferPrintView: React.FC<TransferPrintViewProps> = ({
  lang = 'al',
  received = false,
  company,
  transferNumber,
  date,
  statusId,
  fromLocation,
  toLocation,
  issuedBy,
  receivedByName,
  rows = [],
}) => {
  const t = LABELS[lang];
  const locale = lang === 'al' ? 'de-DE' : 'en-US';
  const qty = (n: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(Number(n) || 0);

  const statusLabel = statusId ? STATUS[lang][statusId] ?? '' : '';
  const detailRows = Array.isArray(rows)
    ? rows.filter((r) => r && (r.productName || r.sku))
    : [];

  const transferRef =
    transferNumber != null && transferNumber !== '' ? `#${transferNumber}` : '–';
  const totalSent = detailRows.reduce((acc, r) => acc + (Number(r.sentQty) || 0), 0);
  const totalDelivered = detailRows.reduce((acc, r) => acc + (Number(r.receivedQty) || 0), 0);
  const totalVariance = totalDelivered - totalSent;

  const metaLabel: React.CSSProperties = {
    fontSize: '10px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: MUTED,
    fontWeight: 600,
  };
  const th: React.CSSProperties = { ...metaLabel, padding: '10px 8px', textAlign: 'left' };
  const thRight: React.CSSProperties = { ...th, textAlign: 'right' };
  const td: React.CSSProperties = {
    padding: '11px 8px',
    color: INK,
    borderBottom: `1px solid ${LINE}`,
    verticalAlign: 'top',
  };
  const tdRight: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  const colSpan = received ? 7 : 5;

  // Company block: name (bold), number, address lines, city + postcode.
  const companyBlock = (nameSize = '20px') => {
    const addr = join([company?.addressLine1, company?.addressLine2]);
    const cityLine = join([company?.city, company?.postalCode]);
    return (
      <>
        <div style={{ fontWeight: 700, fontSize: nameSize, color: INK }}>
          {company?.companyName || '–'}
        </div>
        {company?.companyNumber && (
          <div style={{ color: INK, fontSize: '12px' }}>{company.companyNumber}</div>
        )}
        {addr && <div style={{ color: MUTED, fontSize: '12px' }}>{addr}</div>}
        {cityLine && <div style={{ color: MUTED, fontSize: '12px' }}>{cityLine}</div>}
      </>
    );
  };

  return (
    <div
      style={{
        width: '760px',
        minHeight: '1040px',
        boxSizing: 'border-box',
        padding: '44px 44px 32px',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Inter, Helvetica, Arial, sans-serif',
        fontSize: '13px',
        lineHeight: 1.45,
        color: INK,
        background: '#fff',
      }}
    >
      {/* Letterhead: issuer ("From" = company) left + document meta right */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          paddingBottom: '20px',
          borderBottom: `2px solid ${ACCENT}`,
        }}
      >
        <div style={{ maxWidth: '58%' }}>
          <div style={{ ...metaLabel, marginBottom: '6px' }}>{t.from}</div>
          {companyBlock()}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 700, fontSize: '15px', color: ACCENT, letterSpacing: '0.04em' }}>
            {t.title}
          </div>
          <div style={{ marginTop: '8px', fontSize: '12px' }}>
            <div style={{ color: MUTED }}>
              {t.transferNumber}: <span style={{ color: INK, fontWeight: 700 }}>{transferRef}</span>
            </div>
            <div style={{ color: MUTED, marginTop: '2px' }}>
              {t.date}: <span style={{ color: INK, fontWeight: 600 }}>{date ?? '–'}</span>
            </div>
            {statusLabel && (
              <div style={{ marginTop: '6px' }}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 10px',
                    borderRadius: '999px',
                    backgroundColor: ACCENT_SOFT,
                    color: ACCENT,
                    fontSize: '11px',
                    fontWeight: 700,
                  }}
                >
                  {statusLabel}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Parties: source location + destination location */}
      <div style={{ display: 'flex', gap: '32px', marginTop: '26px', marginBottom: '24px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ ...metaLabel, marginBottom: '6px' }}>{t.issuedFrom}</div>
          <div style={{ fontWeight: 700, fontSize: '14px', color: INK }}>{fromLocation || '–'}</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ ...metaLabel, marginBottom: '6px' }}>{t.issuedTo}</div>
          <div style={{ fontWeight: 700, fontSize: '14px', color: INK }}>{toLocation || '–'}</div>
        </div>
      </div>

      {/* Line table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${LINE}` }}>
        <thead>
          <tr style={{ backgroundColor: '#F7F8F9', borderBottom: `1px solid ${LINE}` }}>
            <th style={{ ...th, width: '30px' }}>{t.no}</th>
            <th style={{ ...th, width: '84px' }}>{t.sku}</th>
            <th style={th}>{t.product}</th>
            <th style={{ ...th, width: '54px' }}>{t.uom}</th>
            <th style={{ ...thRight, width: '52px' }}>{t.qty}</th>
            {received && <th style={{ ...thRight, width: '72px' }}>{t.delivered}</th>}
            {received && <th style={{ ...thRight, width: '72px' }}>{t.variance}</th>}
          </tr>
        </thead>
        <tbody>
          {detailRows.length === 0 ? (
            <tr>
              <td style={{ ...td, textAlign: 'center', color: MUTED }} colSpan={colSpan}>
                {t.noItems}
              </td>
            </tr>
          ) : (
            detailRows.map((row, idx) => {
              const sent = Number(row.sentQty ?? 0);
              const delivered = Number(row.receivedQty ?? 0);
              const variance = delivered - sent;
              let varianceNode: React.ReactNode = '/';
              let varianceColor = MUTED;
              if (variance < 0) {
                varianceNode = `- ${qty(Math.abs(variance))}`;
                varianceColor = '#BA0E13';
              } else if (variance > 0) {
                varianceNode = `+ ${qty(variance)}`;
                varianceColor = '#1B8A5A';
              }
              return (
                <tr key={idx} style={{ backgroundColor: idx % 2 === 1 ? '#FBFBFC' : '#fff' }}>
                  <td style={{ ...td, color: MUTED }}>{idx + 1}</td>
                  <td style={td}>{row.sku ?? '–'}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{row.productName ?? '–'}</td>
                  <td style={{ ...td, color: MUTED }}>{row.uomName ?? '–'}</td>
                  <td style={tdRight}>{qty(sent)}</td>
                  {received && <td style={tdRight}>{qty(delivered)}</td>}
                  {received && (
                    <td style={{ ...tdRight, color: varianceColor, fontWeight: 600 }}>{varianceNode}</td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* Summary */}
      {detailRows.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: '280px', fontVariantNumeric: 'tabular-nums' }}>
            <tbody>
              <tr>
                <td style={{ padding: '5px 0', color: MUTED }}>{t.items}</td>
                <td style={{ padding: '5px 0', textAlign: 'right', color: INK }}>{detailRows.length}</td>
              </tr>
              <tr>
                <td style={{ padding: '5px 0 10px', color: MUTED }}>{t.totalQty}</td>
                <td style={{ padding: '5px 0 10px', textAlign: 'right', color: INK }}>{qty(totalSent)}</td>
              </tr>
              {received && (
                <>
                  <tr>
                    <td style={{ padding: '10px 0 0', borderTop: `2px solid ${ACCENT}`, color: INK, fontWeight: 700 }}>
                      {t.delivered}
                    </td>
                    <td
                      style={{
                        padding: '10px 0 0',
                        borderTop: `2px solid ${ACCENT}`,
                        textAlign: 'right',
                        fontWeight: 700,
                        color: ACCENT,
                      }}
                    >
                      {qty(totalDelivered)}
                    </td>
                  </tr>
                  {totalVariance !== 0 && (
                    <tr>
                      <td style={{ padding: '5px 0', color: MUTED }}>{t.variance}</td>
                      <td
                        style={{
                          padding: '5px 0',
                          textAlign: 'right',
                          fontWeight: 600,
                          color: totalVariance < 0 ? '#BA0E13' : '#1B8A5A',
                        }}
                      >
                        {`${totalVariance < 0 ? '- ' : '+ '}${qty(Math.abs(totalVariance))}`}
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Spacer — pushes signatures + footer to the bottom of the page */}
      <div style={{ flex: 1, minHeight: '48px' }} />

      {/* Authorization */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '40px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ height: '24px', fontWeight: 600 }}>{issuedBy ?? ''}</div>
          <div style={{ borderTop: `1px solid ${INK}`, paddingTop: '6px', ...metaLabel }}>{t.issuedBy}</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ height: '24px', fontWeight: 600 }}>{received ? receivedByName ?? '' : ''}</div>
          <div style={{ borderTop: `1px solid ${INK}`, paddingTop: '6px', ...metaLabel }}>{t.acceptedBy}</div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '28px',
          paddingTop: '12px',
          borderTop: `1px solid ${LINE}`,
          fontSize: '10px',
          color: MUTED,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FinexLogo height={15} />
          <span>www.meetfinex.com</span>
        </span>
        <span>1 / 1</span>
      </div>
    </div>
  );
};

export default TransferPrintView;
